import React from "react";
import Label from "@/components/form/Label";
import { TextField, SelectField } from "@/components/form/fields";
import { formatZipCode, validateZipCode } from "../validation";
import { US_STATES } from "@/utils/constants/stateConstants";

export interface AddressFieldsProps {
  values: {
    streetAddress: string;
    addressLine2?: string;
    city: string;
    state: string;
    zipCode: string;
  };
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onStateChange?: (value: string) => void;
  errors?: {
    streetAddress?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  className?: string;
}

const AddressFieldSet: React.FC<AddressFieldsProps> = ({
  values,
  onChange,
  onStateChange,
  errors = {},
  className = "",
}) => {
  const handleStateChange = (value: string) => {
    if (onStateChange) {
      onStateChange(value);
    }
  };

  // Handle state change for SelectField
  const handleStateSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    handleStateChange(e.target.value);
  };

  // Handle ZIP code change - let user type directly
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers and dash
    const cleanValue = e.target.value.replace(/[^\d-]/g, '');
    
    if (cleanValue !== e.target.value) {
      // If we stripped characters, create a new event with cleaned value
      const newEvent = {
        ...e,
        target: {
          ...e.target,
          value: cleanValue
        }
      } as React.ChangeEvent<HTMLInputElement>;
      
      onChange(newEvent);
    } else {
      // Otherwise pass the event through unchanged
      onChange(e);
    }
  };

  // Format ZIP code on blur
  const handleZipBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const formatted = formatZipCode(e.target.value);
    const zipValue = formatted.replace('-', '');
    
    // Custom validation for 5 or 9 digits
    let isValid = true;
    let errorMessage = '';
    
    if (zipValue.length > 0 && zipValue.length !== 5 && zipValue.length !== 9) {
      isValid = false;
      errorMessage = `ZIP code must have exactly 5 digits or 9 digits (ZIP+4). You entered ${zipValue.length} digits.`;
    } else if (zipValue.length > 0) {
      // Use the standard ZIP code validation
      const validationResult = validateZipCode(formatted);
      isValid = validationResult.valid;
      errorMessage = validationResult.message;
    }
    
    // Update the formatted value
    if (formatted !== e.target.value) {
      // Create a synthetic change event to update the field value
      const changeEvent = {
        target: {
          name: e.target.name,
          value: formatted
        }
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      
      onChange(changeEvent);
    }
    
    // Report validation status to parent
    const errorEvent = {
      target: {
        name: e.target.name + '_error',
        value: errorMessage
      }
    } as unknown as React.ChangeEvent<HTMLInputElement>;
    
    // NOTE: Removed problematic validation events through onChange
    // These were causing form fields to display 'true'/'false' instead of actual values
    // Validation is already handled through the onValidationChange callback above
    
    // Only send error message if there is an error
    if (!isValid) {
      onChange(errorEvent);
    }
  };

  return (
    <div className={`grid grid-cols-1 gap-6 md:grid-cols-2 ${className}`}>
      {/* Street Address Field */}
      <div className="md:col-span-2">
        <Label htmlFor="streetAddress" required>
          Street Address
        </Label>
        <TextField
          id="streetAddress"
          name="streetAddress"
          type="text"
          placeholder="123 Main St"
          value={values.streetAddress}
          onChange={onChange}
          error={!!errors.streetAddress}
          hint={errors.streetAddress}
          required
        />
      </div>
      
      {/* Address Line 2 Field */}
      <div className="md:col-span-2">
        <Label htmlFor="addressLine2">
          Suite/Unit Number
        </Label>
        <TextField
          id="addressLine2"
          name="addressLine2"
          type="text"
          placeholder="Suite 101, Unit B, etc."
          value={values.addressLine2 || ""}
          onChange={onChange}
          error={!!errors.addressLine2}
          hint={errors.addressLine2}
        />
      </div>
      
      {/* City Field */}
      <div>
        <Label htmlFor="city" required>
          City
        </Label>
        <TextField
          id="city"
          name="city"
          type="text"
          placeholder="Enter city"
          value={values.city}
          onChange={onChange}
          error={!!errors.city}
          hint={errors.city}
          required
        />
      </div>
      
      {/* State Field */}
      <div>
        <SelectField
          id="state"
          name="state"
          label="State"
          options={US_STATES}
          value={values.state}
          onChange={handleStateSelectChange}
          error={errors.state}
          required
        />
      </div>
      
      {/* ZIP Code Field */}
      <div>
        <Label htmlFor="zipCode" required>
          ZIP Code
        </Label>
        <TextField
          id="zipCode"
          name="zipCode"
          type="text"
          placeholder="12345"
          value={values.zipCode}
          onChange={handleZipChange}
          onBlur={handleZipBlur}
          error={!!errors.zipCode}
          hint={errors.zipCode}
          required
        />
      </div>
    </div>
  );
};

export default AddressFieldSet;